import React, { useMemo } from "react";
import {
  Geographies,
  Geography,
  Graticule,
  Marker,
  Sphere,
  ZoomableGroup,
} from "react-simple-maps";
import { FaMapMarkerAlt } from "react-icons/fa";
import styled from "styled-components";
import { countryPresenceForList } from "utils/country-presence";
import {
  CountryCoverageMapMapCanvas,
  CountryCoverageMapContentStack,
  CountryCoverageMapGridLayout,
  CountryCoverageMapSection,
  CountryCoverageMapInfoRow,
  CountryCoverageMapGridLayoutSecondary,
  CountryCoverageMapPanelCard,
  CountryCoverageMapMediaSurface,
  CountryCoverageMapMediaSurfaceSecondary,
  CountryCoverageMapSectionBody,
  CountryCoverageMapMapRegionGroup,
  CountryCoverageMapMetaText,
  CountryCoverageMapEyebrowTextSecondary,
  CountryCoverageMapBodyTextSecondary,
  CountryCoverageMapEyebrowTextTertiary,
  CountryCoverageMapTitleText,
  CountryCoverageMapMetaTextSecondary,
  CountryCoverageMapEyebrowText,
  CountryCoverageMapBodyText,
  CountryCoverageMapTextBlock,
} from "components/admin/country-coverage-map/styles/index.inline.styles";
const worldGeographyUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
const StyledGeography = styled(Geography)`
  fill: rgba(247, 242, 231, 0.22);
  outline: none;
  transition: fill 160ms ease;

  &:hover {
    fill: rgba(247, 242, 231, 0.26);
    outline: none;
  }

  &:active {
    fill: rgba(247, 242, 231, 0.3);
    outline: none;
  }
`;
type CountryCoverageMapProps = {
  countries: string[];
  activeCountry?: string | null;
  onCountrySelect?: (countryName: string) => void;
};
export const CountryCoverageMap: React.FC<CountryCoverageMapProps> = ({
  countries,
  activeCountry,
  onCountrySelect,
}) => {
  const countryRecords = useMemo(() => countryPresenceForList(countries), [countries]);
  const mappedCountries = countryRecords.filter(
    (
      country,
    ): country is {
      name: string;
      sourceName: string;
      key: string;
      mapped: true;
      latitude: number;
      longitude: number;
      region: string;
    } => country.mapped,
  );
  const unmappedCountries = countryRecords.filter((country) => !country.mapped);
  const selectedMappedCountry =
    mappedCountries.find((country) => country.sourceName === activeCountry) ||
    mappedCountries[0] ||
    null;
  const mapCenter = selectedMappedCountry
    ? ([selectedMappedCountry.longitude, selectedMappedCountry.latitude] as [number, number])
    : ([18, 8] as [number, number]);
  const mapZoom = selectedMappedCountry ? 1.18 : 1;
  return (
    <CountryCoverageMapContentStack>
      <CountryCoverageMapMediaSurface>
        <CountryCoverageMapMediaSurfaceSecondary>
          <CountryCoverageMapSectionBody>
            <CountryCoverageMapEyebrowText>Active Coverage</CountryCoverageMapEyebrowText>
            <CountryCoverageMapBodyText>
              {selectedMappedCountry?.name || activeCountry || "No mapped country"}
            </CountryCoverageMapBodyText>
            <CountryCoverageMapTextBlock>
              {selectedMappedCountry
                ? `${selectedMappedCountry.region} region`
                : mappedCountries.length > 0
                ? "Select a mapped country to focus the map"
                : "Add a supported country to display coverage pins"}
            </CountryCoverageMapTextBlock>
          </CountryCoverageMapSectionBody>

          <CountryCoverageMapMapCanvas
            aria-label="Country coverage map"
            height={560}
            projection="geoEqualEarth"
            projectionConfig={{
              scale: 190,
            }}
            width={1000}
          >
            <defs>
              <filter id="athspire-marker-shadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="10" floodColor="rgba(5,12,20,0.28)" stdDeviation="12" />
              </filter>
            </defs>

            <rect fill="rgba(15,20,27,0.98)" height="560" width="1000" />
            <Sphere
              fill="rgba(255,255,255,0.02)"
              stroke="rgba(255,255,255,0.18)"
              strokeWidth={0.9}
            />
            <Graticule stroke="rgba(255,255,255,0.08)" strokeWidth={0.45} />

            <ZoomableGroup center={mapCenter} zoom={mapZoom}>
              <Geographies geography={worldGeographyUrl}>
                {({ geographies }) =>
                  geographies.map((geography) => (
                    <StyledGeography
                      geography={geography}
                      key={geography.rsmKey}
                      stroke="rgba(255,255,255,0.16)"
                      strokeWidth={0.55}
                    />
                  ))
                }
              </Geographies>

              {mappedCountries.map((country) => {
                const isActive = activeCountry === country.sourceName;
                return (
                  <Marker coordinates={[country.longitude, country.latitude]} key={country.key}>
                    <CountryCoverageMapMapRegionGroup
                      onClick={() => onCountrySelect?.(country.sourceName)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onCountrySelect?.(country.sourceName);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <circle
                        cx={0}
                        cy={0}
                        fill={isActive ? "rgba(247, 200, 103, 0.24)" : "rgba(211, 166, 60, 0.18)"}
                        filter="url(#athspire-marker-shadow)"
                        r={isActive ? 24 : 16}
                      />
                      <circle
                        cx={0}
                        cy={0}
                        fill={isActive ? "#f7c867" : "#caa25a"}
                        r={isActive ? 8 : 6}
                        stroke="#ffffff"
                        strokeWidth={isActive ? 3 : 2}
                      />
                      <text
                        fill="#f8f4ee"
                        fontFamily="Exo 2, inter, sans-serif"
                        fontSize={isActive ? 13 : 11}
                        fontWeight={600}
                        pointerEvents="none"
                        textAnchor="middle"
                        y={isActive ? -16 : -14}
                      >
                        {country.name}
                      </text>
                    </CountryCoverageMapMapRegionGroup>
                  </Marker>
                );
              })}
            </ZoomableGroup>
          </CountryCoverageMapMapCanvas>
        </CountryCoverageMapMediaSurfaceSecondary>
      </CountryCoverageMapMediaSurface>

      <CountryCoverageMapGridLayout>
        <CountryCoverageMapSection>
          <CountryCoverageMapInfoRow>
            {mappedCountries.length > 0 ? (
              mappedCountries.map((country) => {
                const isActive = activeCountry === country.sourceName;
                return (
                  <button
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm transition ${
                      isActive
                        ? "border-[var(--brand-accent)] bg-[var(--brand-accent)]/12 text-[var(--brand-accent-deep)]"
                        : "border-[var(--brand-border)] bg-[rgba(255,255,255,0.04)] text-[var(--brand-ink)] hover:border-[var(--brand-highlight)]"
                    }`}
                    key={country.key}
                    onClick={() => onCountrySelect?.(country.sourceName)}
                    type="button"
                  >
                    <FaMapMarkerAlt size={12} />
                    {country.name}
                  </button>
                );
              })
            ) : (
              <CountryCoverageMapMetaText>
                No mapped countries are available for the current filter.
              </CountryCoverageMapMetaText>
            )}
          </CountryCoverageMapInfoRow>
        </CountryCoverageMapSection>

        <CountryCoverageMapSection>
          <CountryCoverageMapEyebrowTextSecondary>
            Coverage summary
          </CountryCoverageMapEyebrowTextSecondary>
          <CountryCoverageMapBodyTextSecondary>
            The map now uses a proper world geography layer so country pins sit on a recognisable
            global layout instead of placeholder land shapes.
          </CountryCoverageMapBodyTextSecondary>
          <CountryCoverageMapGridLayoutSecondary>
            <CountryCoverageMapPanelCard>
              <CountryCoverageMapEyebrowTextTertiary>Mapped</CountryCoverageMapEyebrowTextTertiary>
              <CountryCoverageMapTitleText>{mappedCountries.length}</CountryCoverageMapTitleText>
            </CountryCoverageMapPanelCard>
            <CountryCoverageMapPanelCard>
              <CountryCoverageMapEyebrowTextTertiary>
                Unmapped
              </CountryCoverageMapEyebrowTextTertiary>
              <CountryCoverageMapTitleText>{unmappedCountries.length}</CountryCoverageMapTitleText>
            </CountryCoverageMapPanelCard>
          </CountryCoverageMapGridLayoutSecondary>
          {unmappedCountries.length > 0 ? (
            <CountryCoverageMapMetaTextSecondary>
              Unmapped entries: {unmappedCountries.map((country) => country.name).join(", ")}
            </CountryCoverageMapMetaTextSecondary>
          ) : null}
        </CountryCoverageMapSection>
      </CountryCoverageMapGridLayout>
    </CountryCoverageMapContentStack>
  );
};
export default CountryCoverageMap;
